import type { UserAccountType } from "@fraction/shared";
import { FractionClient, type PlatformAdapter } from "@fraction/shared-frontend";
import { SESSION_COOKIE, saveSessionCookie } from "src/auth/sessionCookie";
import config from "src/config";
import { Logger } from "src/log";
import { currentBorrowerSession } from "src/utilities/borrowerSession";
import { getCookie, getStoredCookies } from "src/utilities/tracking";

const LOGGER = new Logger("api.fraction");

const browserAdapter: PlatformAdapter = {
  env: {
    apiBaseUrl: config.baseURL,
    websocketUrl: "",
  },
  logger: LOGGER,
  auth: {
    getActiveBorrowerSession: () => currentBorrowerSession() ?? null,
    getStoredAuthToken: () => getCookie(SESSION_COOKIE) ?? null,
    persistAuthToken: saveSessionCookie,
    getCookieRequestHeaders: () => {
      const cookies = getStoredCookies();
      const headers: Record<string, string> = {};
      if (cookies) headers["fraction-cookies"] = JSON.stringify(cookies);
      return headers;
    },
  },
  storage: {
    getToken: () => (typeof localStorage !== "undefined" ? localStorage.getItem("token") : null),
    setToken: (token) => {
      if (typeof localStorage !== "undefined") localStorage.setItem("token", token);
    },
    clearToken: () => {
      if (typeof localStorage !== "undefined") localStorage.removeItem("token");
    },
    getItem: (key) => (typeof localStorage !== "undefined" ? localStorage.getItem(key) : null),
    setItem: (key, value) => {
      if (typeof localStorage !== "undefined") localStorage.setItem(key, value);
    },
    removeItem: (key) => {
      if (typeof localStorage !== "undefined") localStorage.removeItem(key);
    },
  },
  alerts: {
    toast: () => {},
  },
};

const fractionSingleton = new FractionClient(browserAdapter);

// Seed the account view from the cookie so requests fired before
// `useAccountViewPreference` mounts (the initial user/application fetches) are
// already scoped to the right view; the hook keeps it in sync from then on.
const storedPreference = getCookie("user_preference") as UserAccountType | undefined;
if (storedPreference && storedPreference !== "none") {
  fractionSingleton.setAccountType(storedPreference);
}

export type {
  AnswerIDVerificationArgs,
  BankStatementIncomeCategory,
  BmoDeliveryReceiptCheckResponse,
  BmoDeliveryTriggerResponse,
  BmoManualPollMailbox,
  BmoManualPollResponse,
  BmoOutboundReconciliationResolution,
  BmoOutboundReconciliationResponse,
  BmoPaymentFileDetail,
  BmoPaymentFileFilters,
  BmoPaymentFileRematchResponse,
  BmoPaymentFileReplayResponse,
  BmoPaymentFileReplayState,
  BmoPaymentFileReplayStatus,
  BmoPaymentFileRow,
  BmoPaymentFileSummary,
  BmoPaymentFilesResponse,
  ChecklistApp,
  FinmoValidation,
  GroupingLtvOverTimeData,
  GroupingPropertyValueOverTimeData,
  IncomeExplanation,
  InitiateIDVerificationArgs,
  LinkedBmoPaymentFile,
  LinkedBmoPaymentFileRole,
  MagicUploadExtraction,
  MagicUploadMatch,
  PendingBmoPaymentFilePreview,
  PinSearchResult,
  PortfolioFilters,
  QuickbooksConnectionSummary,
  QuickbooksIncomeDerivation,
  StatementLineProvenance,
  StatementReviewAgentStatement,
  StatementReviewApplyInput,
  StatementReviewApplyResult,
  StatementReviewCluster,
  StatementReviewClusterMember,
  StatementReviewClusterQuestion,
  StatementReviewIncomeCalculationDraft,
  StatementReviewLine,
  StatementReviewOperation,
  StatementReviewSummary,
  StoredDocumentPageFeatures,
  StoredDocumentTyping,
  TransactionBmoFile,
} from "@fraction/shared-frontend";

export default fractionSingleton;
